// ---------------------------------------------------------------------------
// 止めの画面（2026-10-15 射場さんの決定、#186、docs/75。Figma「招待と会社の追加（10/14）」⑤-1〜4）
//   先へ進めないときに出す1枚。型は1つ＝ロゴ／絵（132px の丸い地に簡単な形）／題／本文／補足（任意）／主ボタン1つ＋脇ボタン1つまで。
//   画面は apps/web の components/StopScreen.tsx。場面ごとに変わるのは 絵・文・ボタンだけ（余白・文字・ボタンの位置は全場面で同じ）。
//   文は業務の言葉で簡潔に（docs/10 §5.1.2 の最後の行：招待者・作成者・利用者・現在・作成・設定・削除・閲覧のみ・利用できる・操作してください）
// ---------------------------------------------------------------------------

/** メンテナンス中の応答の code（API の 503。.env の MAINTENANCE_UNTIL か、管理アプリの案内の停止期間のあいだ＝docs/95） */
export const MAINTENANCE_CODE = "MAINTENANCE";

/** 「〇月〇日 〇:〇〇」（日本時間）。読めない日時は null */
export function maintenanceEndText(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("ja-JP", { timeZone: "Asia/Tokyo", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  ) as Record<string, string>;
  return `${p.month}月${p.day}日 ${p.hour}:${p.minute}`;
}

export const STOP_MSG = {
  /** a) PC 専用の機能をスマホで開いた（contract の PC_ONLY_FEATURES の道を 767px 以下・スマホ版で開いた） */
  pcOnly: {
    title: "パソコンから操作してください",
    body: "この機能はスマートフォンでは利用できません。パソコンまたはタブレットで同じ URL を開いてください。",
    note: "対象：料金プラン・新しい会社の作成・帳票デザイン・退会",
    primary: "ホームへ",
    copy: "URL コピー",
    copied: "URL をコピーしました",
    copyFailed: "URL をコピーできませんでした",
  },
  /** b) 案内（旧 招待）の有効期限切れ（#287、docs/100：「招待」をやめた） */
  inviteExpired: {
    title: "案内の有効期限が切れました",
    body: "追加者に案内メールの再送を依頼してください。",
    primary: "ログイン画面へ",
  },
  /** 案内のリンクが読めない・取り消された・使用済み（b と同じ役割。#186 で自分で決めた文。docs/75 §3 → #287） */
  inviteInvalid: {
    title: "案内のリンクが無効です",
    body: "追加者に案内メールの再送を依頼してください。",
    primary: "ログイン画面へ",
  },
  inviteCancelled: {
    title: "この追加は取り消されています",
    body: "心当たりがない場合は、この画面を閉じてください。",
    primary: "ログイン画面へ",
  },
  inviteUsed: {
    title: "この案内は使用済みです",
    body: "登録済みのメールアドレスとパスワードでログインしてください。",
    primary: "ログイン画面へ",
  },
  /** c) 無料トライアルが終わり、閲覧のみ（作成の画面を開いた） */
  readOnly: {
    art: "閲覧のみ",
    title: "閲覧のみの状態です",
    body: "無料トライアルが終了しました。閲覧・PDF・データの持ち出しは可能です。作成・発行には、パソコンの料金プランからお支払い方法を登録してください。",
    primary: "ホームへ",
    export: "データの持ち出し",
    /** 閲覧のみのあいだ画面の上に出す帯（Shell。前の「いまは見るだけです」） */
    bandTitle: "無料トライアルが終了しました。閲覧のみの状態です。",
    bandBody: "閲覧・PDF・データの持ち出しは可能です。作成・修正・発行はできません。",
  },
  /** d) ページが見つからない */
  notFound: {
    title: "ページが見つかりません",
    body: "URL が変更されたか、削除された可能性があります。",
    primary: "ホームへ",
  },
  /**
   * e) メンテナンス中。end＝「〇月〇日 〇:〇〇」（案内の停止期間の終わりか .env の MAINTENANCE_UNTIL。無ければ予定の行を出さない）。
   * 補足（note）に案内の本文の1行目（docs/95 ★10）。終了予定を過ぎて DB がまだ戻らないあいだは overdue（★4）
   */
  maintenance: {
    title: "メンテナンス中です",
    body: (end: string | null) => `${end ? `終了予定：${end}。` : ""}終了後に再度アクセスしてください。`,
    overdue: "終了予定の時刻を過ぎましたが、まだ作業中です。しばらくしてから再度アクセスしてください。",
    primary: "再読み込み",
    /** API の 503 の title（画面は止めの画面を出すので、ほかの所にはふつう出ない） */
    api: "メンテナンス中です。終了後に再度アクセスしてください",
  },
  /** f) 管理者だけの画面を担当者が開いた */
  adminOnly: {
    title: "この操作には管理者の権限が必要です",
    body: "管理者に依頼してください。",
    primary: "ホームへ",
  },
  /** g) 自動ログアウト（#189、docs/76）：無操作の上限を超えた（API の 401 SESSION_IDLE）。ボタンは /login?next=<直前のパス> */
  idle: {
    title: "自動ログアウトしました",
    body: "一定時間操作がなかったため、情報保護のためログアウトしました。再度ログインしてください。",
    primary: "ログイン画面へ",
  },
  /**
   * j) 会社の選択でログインの有効期限が切れた（#203 の続き、2026-10-17 夜 射場さん。API の 401 AUTH_CHOOSE_EXPIRED）：
   * ログインから会社を選ぶまでの鍵（10分・1回きり）が使えない＝どの会社を押しても進めない。b)「招待の有効期限が切れています」と同じ役割
   */
  loginExpired: {
    title: "ログインの有効期限が切れました",
    body: "会社の選択は、ログインから10分以内に行います。再度ログインしてください。",
    primary: "ログイン画面へ",
  },
  /** h) 画面の表示中の例外・部品の読み込みの失敗（#189 A、ルートのエラー境界）。真っ白にしない */
  crashed: {
    title: "画面を読み込み直してください",
    body: "",
    primary: "再読込",
  },
  /** i) ゴミ箱の見積書を開いた（#200、docs/81。API の 404 ESTIMATE_TRASHED）。date＝「10/17」、name＝削除者（いなければ null） */
  trashed: {
    title: "この見積書はゴミ箱にあります",
    body: (date: string, name: string | null) => `削除日 ${date}${name ? `・削除者 ${name}` : ""}`,
    note: "番号のある見積書はゴミ箱に残り、いつでも復元できます",
    primary: "復元",
    secondary: "見積書の一覧",
  },
  /** k) メールの閲覧ページが公開を終えた（#225、docs/91 §3.3。無効にした・公開期限が過ぎた＝API の 410）。company＝送った会社名 */
  deliveryGone: {
    title: "この書類は公開を終了しました",
    body: (company: string) => `公開期限が過ぎたか、送信者が公開を終了しました。必要なときは ${company} にお問い合わせください。`,
  },
  /** l) メールの閲覧ページが見つからない（鍵が違う・途中で切れた＝API の 404。会社名も出さない） */
  deliveryNotFound: {
    title: "ページが見つかりません",
    body: "URL を確かめてください。メールの URL が途中で切れていることがあります。",
  },
  /** m) 承諾ページ（#233、docs/93 §4.4）を無効にした・取り消した・期限切れ（API の 410。発行した会社名だけ） */
  orderGone: {
    title: "この書類は承諾の受付を終了しました",
    expiredTitle: "この書類の承諾期限が過ぎました",
    body: (company: string) => `必要なときは ${company} にお問い合わせください。`,
  },
  /** o) 支払通知書の確認ページ（#233 3b、docs/93 §15.6）を取り消した・出し直した・期限切れ（API の 410。発行した会社名だけ） */
  noticeGone: {
    title: "この書類の確認の受付を終了しました",
    expiredTitle: "この書類の確認ページの期限が過ぎました",
    body: (company: string) => `必要なときは ${company} にお問い合わせください。`,
  },
  /** o) 受領・検収のページ（#266 1b、docs/96）を取り消した・期限切れ（API の 410。発行した会社名だけ） */
  acceptanceGone: {
    title: "この書類の受付を終了しました",
    expiredTitle: "この書類のページの期限が過ぎました",
    body: (company: string) => `必要なときは ${company} にお問い合わせください。`,
  },
  /** n) 前の回の承諾ページ（#233 2b：訂正版を出した＝API の 410 superseded。{name}＝帳票の名前・next＝新しい承諾ページがあるか） */
  orderRevised: {
    title: (name: string) => `新しい${name}が届いています`,
    body: (company: string, name: string, next: boolean) =>
      next ? `${company} から内容を変更した${name}が届いています。新しい${name}を確かめて承諾してください。` : `${company} から内容を変更した${name}が届いています。${company} から届く新しい${name}をご確認ください。`,
    open: (name: string) => `新しい${name}を開く`,
  },
} as const;
